import { Component, effect, Inject, inject, signal, untracked } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogClose, MatDialogActions } from '@angular/material/dialog';
import { ToastService } from '@app/core/services/toast.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { DeliveryNotesService } from 'api/services';
import { DeliveryNotesV3Service } from 'api-v3/services';
import { SortType } from 'api-v3/models';
import { lastValueFrom } from 'rxjs';
import { FolderData } from '@app/shared/models/table-data-types';
import { DeliveryNoteData } from '@app/shared/models/store-types';
import { SearchStore } from '@app/stores/searchStore';
import { SelectionStore } from '@app/stores/selectionStore';
import { GenericTableComponent } from '@app/shared/components/generic-table/generic-table.component';
import { ColumnCreator } from '@app/shared/services/view-configs/column-creator';
import {
    SortEvent,
    TableColumn,
    TableConfiguration,
} from '@app/shared/models/generic-table.interface';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-add-notes-to-bundle-dialog',
    templateUrl: './add-notes-to-bundle-dialog.component.html',
    styleUrls: ['./add-notes-to-bundle-dialog.component.scss'],
    // Scoped store instances so the embedded generic table's pagination and
    // selection state don't clobber the view behind the dialog.
    providers: [SearchStore, SelectionStore],
    imports: [
        MatDialogTitle,
        MatIconButton,
        MatDialogClose,
        MatIcon,
        GenericTableComponent,
        MatDialogActions,
        MatButton,
        TranslateModule,
    ],
})
export class AddNotesToBundleDialogComponent {
    protected readonly searchStore = inject(SearchStore);
    protected readonly selectionStore = inject(SelectionStore);

    tableData = signal<DeliveryNoteData[]>([]);
    tableLoading = signal(false);
    loading = signal(false);

    organizationSiteKey: string;

    columns: TableColumn[] = [
        ColumnCreator.select({
            selectableCondition: item =>
                !(item as DeliveryNoteData).deliveryNoteBundleKey,
        }),
        ColumnCreator.deliveryNoteNumber(),
        ColumnCreator.orderNumber(),
        ColumnCreator.deliveryNoteBundleNumber(),
    ] as TableColumn[];

    tableConfig: TableConfiguration = {
        columns: this.columns,
        features: {
            sortable: true,
            paginated: true,
            pagination: true,
            selectable: true,
        },
        paginationOptions: [10, 20, 50],
        autoReload: { enabled: false },
        trackBy: (_index, item) => (item as DeliveryNoteData)._key,
    };

    constructor(
        @Inject(MAT_DIALOG_DATA)
        private data: {
            deliveryNoteBundle: FolderData;
            organizationSiteKey: string;
        },
        private deliveryNotesService: DeliveryNotesService,
        private deliveryNotesV3Service: DeliveryNotesV3Service,
        private dialogRef: MatDialogRef<AddNotesToBundleDialogComponent>,
        private toastr: ToastService,
        private translateService: TranslateService
    ) {
        this.organizationSiteKey = data.organizationSiteKey;

        // Initial load plus reload whenever the (dialog-scoped) pagination or
        // sort state triggers a refresh.
        effect(() => {
            this.searchStore.refreshTrigger();
            untracked(() => void this.getTableData());
        });
    }

    onSortChange(event: SortEvent): void {
        if (!event.direction) {
            return;
        }
        this.searchStore.updateSort(
            event.key as Parameters<typeof this.searchStore.updateSort>[0],
            event.direction
        );
        this.searchStore.triggerDataRefresh();
    }

    async addNotesToBundle(): Promise<void> {
        // The dialog-scoped SelectionStore only ever holds rows of this table,
        // so every selected item is a delivery note.
        const notes = (
            this.selectionStore.selectedItems() as DeliveryNoteData[]
        ).filter(note => !note.deliveryNoteBundleKey);

        if (notes.length === 0) {
            return;
        }
        this.loading.set(true);

        try {
            await lastValueFrom(
                this.deliveryNotesService.postExistingDeliveryNoteIntoExistingBundle(
                    {
                        organizationSiteKey: this.organizationSiteKey,
                        deliveryNoteBundleKey:
                            this.data.deliveryNoteBundle._key,
                        body: notes.map(n => n._key),
                    }
                )
            );
            this.loading.set(false);
            this.toastr.success(
                this.translateService.instant(
                    'OUTGOING-GOODS.ADD-TO-BUNDLE-DIALOG.SUCCESS'
                )
            );
            this.dialogRef.close(notes);
        } catch {
            this.loading.set(false);
            this.toastr.error(
                this.translateService.instant(
                    'OUTGOING-GOODS.ADD-TO-BUNDLE-DIALOG.ERROR'
                )
            );
            this.dialogRef.close([]);
        }
    }

    async getTableData(): Promise<void> {
        this.tableLoading.set(true);
        try {
            const pagination = this.searchStore.pagination();
            const sort = this.searchStore.sort();

            const response = await lastValueFrom(
                this.deliveryNotesV3Service.getDeliveryNotesMetadataInPoolV3({
                    organizationSiteKey: this.organizationSiteKey,
                    limit: pagination.limit,
                    offset: pagination.offset,
                    sort:
                        sort.sortDirection === 'asc'
                            ? SortType.Asc
                            : SortType.Desc,
                    sortBy: (sort.sortBy ?? undefined) as Exclude<
                        Parameters<
                            DeliveryNotesV3Service['getDeliveryNotesMetadataInPoolV3']
                        >[0],
                        undefined
                    >['sortBy'],
                })
            );

            this.tableData.set(
                (response?.data ?? []).map(note => ({
                    ...note,
                    orgSiteKey: this.organizationSiteKey,
                    read_only: false,
                }))
            );
            if (response?.paginationInfo) {
                this.searchStore.updatePaginationFromApiResponse(
                    response.paginationInfo
                );
            }
        } catch {
            this.toastr.error(
                this.translateService.instant(
                    'OUTGOING-GOODS.ADD-TO-BUNDLE-DIALOG.FETCHING-ERROR'
                )
            );
        } finally {
            this.tableLoading.set(false);
        }
    }
}
